import { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface KnowledgeContent {
  id: string;
  title: string;
  content: string;
  description?: string;
  tags?: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export default function Conhecimento() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [contents, setContents] = useState<KnowledgeContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<KnowledgeContent | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    description: "",
    tags: "",
  });

  useEffect(() => {
    fetchContents();
  }, []);

  const fetchContents = async () => {
    try {
      const { data, error } = await supabase
        .from('knowledge_base_content')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setContents(data || []);
    } catch (error) {
      console.error('Erro ao buscar conteúdos:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar os conteúdos da base de conhecimento.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) return;

    try {
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const contentData = {
        title: formData.title,
        content: formData.content,
        description: formData.description || null,
        tags: tagsArray.length > 0 ? tagsArray : null,
        created_by: user.id,
      };

      let error;

      if (editingContent) {
        const { error: updateError } = await supabase
          .from('knowledge_base_content')
          .update(contentData)
          .eq('id', editingContent.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('knowledge_base_content')
          .insert(contentData);
        error = insertError;
      }

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: `Conteúdo ${editingContent ? 'atualizado' : 'criado'} com sucesso!`,
      });

      setIsDialogOpen(false);
      setEditingContent(null);
      setFormData({ title: "", content: "", description: "", tags: "" });
      fetchContents();
    } catch (error) {
      console.error('Erro ao salvar conteúdo:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar o conteúdo.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (content: KnowledgeContent) => {
    setEditingContent(content);
    setFormData({
      title: content.title,
      content: content.content,
      description: content.description || "",
      tags: content.tags?.join(', ') || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este conteúdo?')) return;

    try {
      const { error } = await supabase
        .from('knowledge_base_content')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Conteúdo excluído com sucesso!",
      });

      fetchContents();
    } catch (error) {
      console.error('Erro ao excluir conteúdo:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir o conteúdo.",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({ title: "", content: "", description: "", tags: "" });
    setEditingContent(null);
  };

  const filteredContents = contents.filter(content =>
    content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Base de Conhecimento</h1>
          <p className="text-muted-foreground">
            Gerencie os conteúdos da base de conhecimento da IA
          </p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Novo Conteúdo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingContent ? 'Editar Conteúdo' : 'Novo Conteúdo'}
              </DialogTitle>
              <DialogDescription>
                {editingContent ? 'Atualize as informações do conteúdo.' : 'Crie um novo conteúdo para a base de conhecimento.'}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Digite o título do conteúdo"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Breve descrição do conteúdo"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="content">Conteúdo *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Digite o conteúdo completo aqui..."
                  className="min-h-[300px]"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tags">Tags</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="Separe as tags por vírgula (ex: IA, tecnologia, processo)"
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingContent ? 'Atualizar' : 'Criar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center space-x-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar conteúdos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>

      {filteredContents.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">Nenhum conteúdo encontrado</h3>
              <p className="text-muted-foreground">
                {searchTerm 
                  ? "Tente ajustar os termos de busca." 
                  : "Comece criando o primeiro conteúdo da base de conhecimento."
                }
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {filteredContents.map((content) => (
            <Card key={content.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-xl">{content.title}</CardTitle>
                    {content.description && (
                      <CardDescription>{content.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(content)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(content.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {content.tags && content.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {content.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardHeader>
              
              <CardContent>
                <div className="prose max-w-none">
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                    {content.content.length > 300 
                      ? content.content.substring(0, 300) + "..."
                      : content.content
                    }
                  </p>
                </div>
                
                <div className="flex items-center justify-between mt-4 pt-4 border-t text-xs text-muted-foreground">
                  <span>
                    Criado em: {new Date(content.created_at).toLocaleDateString('pt-BR')}
                  </span>
                  <span>
                    Atualizado em: {new Date(content.updated_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}